import type {
    DropdownData,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from '@fitogest/reactive-form-builder/utils';

import { ClienteModel } from 'src/app/models/cliente.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ProductModel } from 'src/app/models/productos/product.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';

import { EntradasEnvironmentDefinition } from '../../environment-variables';

export class EntradasListsDefinition {

    public readonly env: EntradasEnvironmentDefinition = new EntradasEnvironmentDefinition();

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);

    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);

    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public parcelasDestino: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public productos: DropdownData<ProductModel | VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);

    public productosAll: DropdownData<ProductModel> = list<ProductModel>(['...', null]);

    public cultivosAll: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);

    public auxCultivos: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);

    public comercios: DropdownData<Compradores> = list<Compradores>(['...', null]);

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>(['...', null]);

    public articulos: DropdownData<ProductModel> = list<ProductModel>(['...', null]);

    public tiposDeDato: DropdownData<string> = this.buildTiposDeDato();

    public tipoEntrada: DropdownData<string> = this.buildTipoEntrada();

    public motivos: DropdownData<string> = list<string>(
        ['...', null],
        'Desperdicio',
        'Mala calidad',
        'Falta de agua',
        'Buen estado',
        'Movimiento',
        'Arranque'
    );

    public motivosCovidai: DropdownData<string> = list<string>(
        ['...', null],
        'Desperdicio',
        '1ª',
        '2ª',
        'Falta de agua',
        'Reposición',
        'Climatología Adversa',
        'Movimiento'
    );

    public ubicaciones: DropdownData<string> = list<string>(
        ['...', null],
        'I-1',
        'I-2',
        'I-3',
        'I-4',
        'I-5',
        'I-6',
        'Sombrajos 1',
        'Sombrajos 2',
        'Sombrajos 3',
        'Sombrajos 4'
    );

    public ubicacionesViveros: DropdownData<string> = list<string>(
        ['...', null],
        'I-1',
        'I-2',
        'I-3',
        'I-4',
        'I-5',
        'I-6',
        'S-1',
        'S-2',
        'S-3',
        'S-4'
    );

    public listaSemilla: DropdownData<string> = list<string>(['...', null], 'R1', 'R2', 'S');

    public unidades: DropdownData<string> = list<string>(['...', null], 'Kg', 'Gr', 'L', 'Ud');

    private buildTiposDeDato(): DropdownData<string> {
        const options: Array<string> = ['Fitosanitario', 'Fertilizante'];

        if (this.env.showStockSemilla || this.env.forcesPlantType || this.env.hasPlantas) {
            options.push('Semilla');
            options.push('Planta');
        }

        if (this.env.appName === 'pyf') {
            options.push('Ganadería', 'Otros');
        }

        if (this.env.appName === 'agrotata'){
            options.push('Bio');
        }

        return list<string>(['...', null], ...options);
    }

    private buildTipoEntrada(): DropdownData<string> {
        const options: Array<[string, string]> = [
            ['Entrada', 'E']
        ];

        if (this.env.appName !== 'fjperan') {
            options.push(['Salida', 'S']);
            options.push(['Producción', 'P']);
        }

        if (this.env.appName === 'sendra') {
            options.push(['Movimiento', 'MOV']);
        }

        if (this.env.appName === 'sendra' || this.env.appName === 'viverosmuzale') {
            options.push(['Merma', 'MER']);
        }

        return list<string>(['...', null], ...options);
    }

    public formatosViverogest: DropdownData<{ tipo: string; codigo: string; uds_bandeja: number }> =
        list<{ tipo: string; codigo: string; uds_bandeja: number }>(
        ['...', null],
        ['AF1L',   { tipo: 'bandeja', codigo: 'AF1L',  uds_bandeja: 15 }],
        ['AF28',   { tipo: 'bandeja', codigo: 'AF28',  uds_bandeja: 28 }],
        ['AF35',   { tipo: 'bandeja', codigo: 'AF35',  uds_bandeja: 35 }],
        ['AF45',   { tipo: 'bandeja', codigo: 'AF45',  uds_bandeja: 45 }],
        ['AF54',   { tipo: 'bandeja', codigo: 'AF54',  uds_bandeja: 54 }],
        ['AF60',   { tipo: 'bandeja', codigo: 'AF60',  uds_bandeja: 60 }],
        ['B77',   { tipo: 'bandeja', codigo: 'B77',   uds_bandeja: 77 }],
        ['B96',   { tipo: 'bandeja', codigo: 'B96',   uds_bandeja: 96 }],
        ['C1L',  { tipo: 'maceta', codigo: 'C1L',    uds_bandeja: 1 }],
        ['C1,2L',  { tipo: 'maceta', codigo: 'C1,2L',  uds_bandeja: 1 }],
        ['C1,25L',  { tipo: 'maceta', codigo: 'C1,25L', uds_bandeja: 1 }],
        ['C1,6L',  { tipo: 'maceta', codigo: 'C1,6L',  uds_bandeja: 1 }],
        ['C2L',  { tipo: 'maceta', codigo: 'C2L',    uds_bandeja: 1 }],
        ['C2,5L',  { tipo: 'maceta', codigo: 'C2,5L',  uds_bandeja: 1 }],
        ['C3L',  { tipo: 'maceta', codigo: 'C3L',    uds_bandeja: 1 }],
        ['C3,5L',  { tipo: 'maceta', codigo: 'C3,5L',  uds_bandeja: 1 }],
        ['C5L',  { tipo: 'maceta', codigo: 'C5L',    uds_bandeja: 1 }],
        ['C7,5L',  { tipo: 'maceta', codigo: 'C7,5L',  uds_bandeja: 1 }],
        ['C10L',  { tipo: 'maceta', codigo: 'C10L',   uds_bandeja: 1 }],
        ['C14L',  { tipo: 'maceta', codigo: 'C14L',   uds_bandeja: 1 }],
        ['C15L',  { tipo: 'maceta', codigo: 'C15L',   uds_bandeja: 1 }],
        ['C17L',  { tipo: 'maceta', codigo: 'C17L',   uds_bandeja: 1 }],
        ['C18L',  { tipo: 'maceta', codigo: 'C18L',   uds_bandeja: 1 }],
        ['C20L',  { tipo: 'maceta', codigo: 'C20L',   uds_bandeja: 1 }],
        ['C22L',  { tipo: 'maceta', codigo: 'C22L',   uds_bandeja: 1 }],
        ['C24L',  { tipo: 'maceta', codigo: 'C24L',   uds_bandeja: 1 }],
        ['C25L',  { tipo: 'maceta', codigo: 'C25L',   uds_bandeja: 1 }],
        ['C35L',  { tipo: 'maceta', codigo: 'C35L',   uds_bandeja: 1 }],
        ['C45L',  { tipo: 'maceta', codigo: 'C45L',   uds_bandeja: 1 }],
        ['C50L',  { tipo: 'maceta', codigo: 'C50L',   uds_bandeja: 1 }],
        ['C55L',  { tipo: 'maceta', codigo: 'C55L',   uds_bandeja: 1 }],
        ['CONT', { tipo: 'maceta', codigo: 'CONT', uds_bandeja: 1 }],
    );
}
